export type WhatsAppCtaType = "chat" | "catalog";

export const WHATSAPP_CHAT_CTA_LABEL = "Chat on WhatsApp";
export const WHATSAPP_CATALOG_CTA_LABEL = "View Catalog on WhatsApp";

function cleanText(value: unknown) {
  return String(value ?? "").trim();
}

export function normalizeWhatsAppCtaType(value: unknown): WhatsAppCtaType {
  return value === "catalog" ? "catalog" : "chat";
}

export function normalizeWhatsAppPhoneDigits(value?: string | null) {
  const raw = cleanText(value);
  if (!raw) return "";

  const cleaned = raw.replace(/[^\d+]/g, "");

  if (/^\+254[17]\d{8}$/.test(cleaned)) {
    return cleaned.replace(/[^\d]/g, "");
  }

  if (/^254[17]\d{8}$/.test(cleaned)) {
    return cleaned;
  }

  if (/^0[17]\d{8}$/.test(cleaned)) {
    return `254${cleaned.slice(1)}`;
  }

  if (/^[17]\d{8}$/.test(cleaned)) {
    return `254${cleaned}`;
  }

  const digits = cleaned.replace(/[^\d]/g, "");
  return /^\d{10,15}$/.test(digits) ? digits : "";
}

export function normalizeWhatsAppChatNumber(value?: string | null) {
  const digits = normalizeWhatsAppPhoneDigits(value);
  return /^254[17]\d{8}$/.test(digits) ? `+${digits}` : "";
}

export function isValidWhatsAppChatNumber(value?: string | null) {
  return !!normalizeWhatsAppChatNumber(value);
}

export function buildWhatsAppChatUrl(whatsappChatNumber?: string | null) {
  const normalized = normalizeWhatsAppChatNumber(whatsappChatNumber);
  const digits = normalized.replace(/[^\d]/g, "");
  return digits ? `https://wa.me/${digits}` : "";
}

export function normalizeWhatsAppCatalogUrl(value?: string | null) {
  const raw = cleanText(value);
  if (!raw) return "";

  const withScheme = /^https?:\/\//i.test(raw)
    ? raw
    : /^(?:www\.)?wa\.me\//i.test(raw)
    ? `https://${raw}`
    : raw;

  const match = withScheme.match(
    /^https?:\/\/(?:www\.)?wa\.me\/c\/(\d{10,15})(?:[/?#].*)?$/i
  );

  return match?.[1] ? `https://wa.me/c/${match[1]}` : "";
}

export function isValidWhatsAppCatalogUrl(value?: string | null) {
  return !!normalizeWhatsAppCatalogUrl(value);
}

export function buildWhatsAppCta(params: {
  whatsappCtaType?: unknown;
  whatsappChatNumber?: string | null;
  whatsappCatalogUrl?: string | null;
}) {
  const requestedType = normalizeWhatsAppCtaType(params.whatsappCtaType);

  if (requestedType === "chat") {
    const chatUrl = buildWhatsAppChatUrl(params.whatsappChatNumber);

    return {
      type: "chat" as const,
      label: WHATSAPP_CHAT_CTA_LABEL,
      url: chatUrl,
    };
  }

  const catalogUrl = normalizeWhatsAppCatalogUrl(params.whatsappCatalogUrl);

  if (catalogUrl) {
    return {
      type: "catalog" as const,
      label: WHATSAPP_CATALOG_CTA_LABEL,
      url: catalogUrl,
    };
  }

  return {
    type: "catalog" as const,
    label: WHATSAPP_CATALOG_CTA_LABEL,
    url: "",
  };
}
